import { defineStore } from "pinia";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import { useCognitoAdmin } from "@/services";
import { Auth } from "aws-amplify";
import { CognitoSettings } from "@/types";

interface AuthState {
  userInfo: Record<string, any> | null;
  accessToken: string;
  isInstanceReady: boolean;
  permissions: string[];
  cognitoSettings: CognitoSettings | null;
}

export const useAuth = defineStore("auth", {
  state: (): AuthState => ({
    userInfo: null,
    accessToken: "",
    isInstanceReady: false,
    permissions: [],
    cognitoSettings: null,
  }),
  getters: {
    user: (state) => (state.userInfo ? { ...state.userInfo.attributes } : null),
    isLoggedIn: (state) => !!state.userInfo,
  },
  actions: {
    async getCurrentUser() {
      this.userInfo = await Auth.currentUserInfo();
    },
    async currentUserInfo() {
      await this.getCurrentUser();
      if (this.isLoggedIn) {
        useCognitoAdmin().showCustomAttributes = true;
        this.accessToken = await Auth.currentSession().then(
          (r: CognitoUserSession) => {
            return r.getAccessToken().getJwtToken();
          }
        );
      } else {
        this.accessToken = "";
      }
    },
    async hasPermission(permission: string): Promise<boolean> {
      if (this.permissions.some((r) => permission.includes(r))) {
        return true;
      }
      return this.getUserPermissions().then(() =>
        this.permissions.some((r) => permission.includes(r))
      );
    },
    async getUserPermissions() {
      await this.currentUserInfo();
      this.permissions = this.isLoggedIn
        ? await Auth.currentSession()
            .then(
              (r: CognitoUserSession) =>
                r.getAccessToken().payload["cognito:groups"]
            )
            .catch(() => [])
        : [];
    },
    setCognitoSettings(settings: CognitoSettings) {
      this.cognitoSettings = settings;
    },
  },
});
